import { Component, Inject } from '@angular/core';
import {
  MenuContributionsService,
  TableContributionsService,
  TableColumn,
  ActionContributionsService,
  WorkspaceTab,
  WorkspaceTabSection,
} from '@ansys-internal/awf-angular/contributions';
import {
  ActiveSectionState,
  SectionSelectAction,
  SectionsState,
  TabOpenAction,
  TabSelectAction,
} from '@ansys-internal/awf-angular/workspace';
import { AppThemeState, State } from '@ansys-internal/awf-angular/store';
import { ConceptService } from './shared/services/concept.service';
import {
  DashboardTables,
  LibraryTableColumns,
  LibraryTableActions,
  CreateDesignInstanceAction,
  OpenResultsAction,
  CreateDesignAction,
  FilesTableColumns,
  INSTANCE_TAB_TYPE,
  CreateDesignOutput,
} from '@ansys-internal/awf-angular/dashboard';
import { lastValueFrom } from 'rxjs';
import { DesignInstance, Job } from '@ansys/cloud-angular-client';
import {
  JobMessageData,
  JobQueued,
  JobStarted,
  JobCreated,
  JobCompleted,
  JobFinished,
  JobMessage,
} from '@ansys-internal/awf-angular/sockets';
import {
  AWFJob,
  AWFDesignInstance,
  SnackBarService,
  NavigateAction,
  TableRefreshDataAction
} from '@ansys-internal/awf-angular/shared';
import {
  AddJob,
  UpdateJobStatus,
  UpdateJob,
  UpdateJobProgress,
  CreateConceptFromTemplateAction,
  UpdateJobTimes,
} from './actions';
import { ActiveConceptIDState } from './state/lib/active-concept-id.state';
import { PlotService } from './shared/services/plot.service';
import {
  defaultAxisColorDarkConfig,
  defaultAxisColorLightConfig,
} from './components/widgets/charts/graph-display/lib/models/default-axis-color-config';
import { CevActionsService } from './shared/services/cev-actions.service';
import { ConceptSections, ConceptSubSection } from './shared/enums';
import { PageStateExecuteEnum } from './state/lib/app-page.state';
import { Router } from '@angular/router';
import { UserSocketService } from '@ansys-internal/awf-angular/sockets';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent {
  constructor(
    private conceptService: ConceptService,
    @Inject(TableContributionsService)
    private tables: TableContributionsService,
    @Inject(MenuContributionsService) private menus: MenuContributionsService,
    private actions: ActionContributionsService,
    private snackbar: SnackBarService,
    private state: State,
    private plotService: PlotService,
    @Inject(INSTANCE_TAB_TYPE) private tabType: string,
    private cevActions: CevActionsService,
    public router: Router,
    private socketService: UserSocketService,
  ) {
    this.actions.get(TabSelectAction).subscribe((tab?: WorkspaceTab) => {
      this.state.set(
        ActiveConceptIDState,
        tab?.urlParams ? tab.urlParams[0] : ''
      );
      // check if state has concept loaded
      if (tab?.urlParams && tab.urlParams[0]) {
        this.conceptService.openConcept(tab.urlParams[0]);
      }
    });

    this.state.get(ActiveSectionState).subscribe(section => {
      if (!section) return;

      this.handleRoutingForSection(section);
    });

    this.jobListeners();

    this.actions
      .get(OpenResultsAction)
      .subscribe((job) => this.openResults(job));

    this.actions
      .get(CreateDesignAction)
      .subscribe(async (context: CreateDesignOutput) => {
        await this.createdDesign(context);
      });

    this.actions
      .get(CreateDesignInstanceAction)
      .subscribe(async (instance: AWFDesignInstance) => {
        await this.createdInstance(instance);
      });

    this.tables
      .getColumn(DashboardTables.LIBRARY, LibraryTableColumns.ACTIONS)
      .then((column: TableColumn) => {
        column.width = 130;
      });

    // END CONTRIBUTIONS

    // AppThemeState subscription
    this.state.get(AppThemeState).subscribe(async (theme) => {
      const isDarkMode =
        theme === 'dark' ||
        (theme === 'auto' &&
          window.matchMedia?.('(prefers-color-scheme: dark)').matches);

      const axisColorConfig = isDarkMode
        ? defaultAxisColorDarkConfig
        : defaultAxisColorLightConfig;

      this.plotService.updateGraphLayout({
        font: {
          color: isDarkMode ? '#fff' : '#000',
        },
        legend: {
          font: {
            color: isDarkMode ? '#fff' : '#000',
          },
        },
        xaxis: axisColorConfig,
        yaxis: axisColorConfig,
      });
    });
  }

  private handleRoutingForSection(section: WorkspaceTabSection): void {
    const updates = [];

    updates.push({
      type: PageStateExecuteEnum.SET_SECTION,
      payload: section.type as ConceptSections,
    });

    if (section.urlParams?.[0] === ConceptSubSection.RESULTS) {
      const subSection = section.urlParams[1];

      if (subSection === ConceptSubSection.COMPARISON_TOOL) {
        updates.push(
          { type: PageStateExecuteEnum.SET_SUBSECTION, payload: subSection },
          {
            type: PageStateExecuteEnum.SET_COMPARISON_TOOL_JOB_IDS,
            payload: section.urlParams?.[2]?.split(',') ?? null,
          }
        );
      } else {
        updates.push(
          {
            type: PageStateExecuteEnum.SET_SUBSECTION,
            payload: ConceptSubSection.RESULTS,
          },
          {
            type: PageStateExecuteEnum.SET_RESULT_ID,
            payload: subSection ?? '',
          },
          {
            type: PageStateExecuteEnum.SET_COMPARISON_TOOL_JOB_IDS,
            payload: [],
          }
        );
      }
    }

    // TODO: This is a temporary fix for batch updating
    updates.forEach(({ type, payload }) => {
      this.cevActions.execute(type, payload);
    });
  }

  private async openResults(job: AWFJob): Promise<void> {
    const instanceId = job.designInstanceId;
    if (!instanceId) {
      throw new Error('No instance found');
    }

    await this.actions.execute(TabOpenAction, {
      type: this.tabType,
      urlParams: [instanceId],
      userData: { id: instanceId },
      selectTab: true,
    });

    // Navigate to new Result
    setTimeout(() => {
      const sections = this.state.value(SectionsState);
      const requirementSection = sections.find(
        (section) => section.type === 'requirement'
      );
      this.actions.execute(NavigateAction, [
        ...(requirementSection?.userData?.urlTree ?? []),
        'results',
        job.jobId + '',
      ]);
    });
  }

  private async createdInstance(instance: AWFDesignInstance): Promise<void> {
    await lastValueFrom(this.conceptService.cloneConcept(instance));
  }

  private async createdDesign(
    createdDesignOutput: CreateDesignOutput
  ): Promise<void> {
    if (createdDesignOutput.templateId) {
      await this.actions.execute(CreateConceptFromTemplateAction, [
        createdDesignOutput.file,
        createdDesignOutput.templateId,
      ]);
    } else {
      const metadata = createdDesignOutput.metadata as Record<string, any>;

      if (
        createdDesignOutput.file.parentDesignId &&
        createdDesignOutput.file.designInstanceList
      ) {
        await lastValueFrom(
          this.conceptService.cloneConcept(
            createdDesignOutput.file.designInstanceList[0] as DesignInstance
          )
        );
      } else {
        if (metadata?.['importFile']) {
          try {
            await this.conceptService.createConceptFromImport(
              createdDesignOutput,
              metadata['importFile']
            );
          } catch (error) {
            this.conceptService
              .deleteDesign(createdDesignOutput.file.designId as string)
              .subscribe({
                next: () => {
                  this.actions.execute(
                    TableRefreshDataAction,
                    DashboardTables.FILES
                  );
                },
                error: () => {
                  this.snackbar.error('Failed to delete design:');
                },
              });
            this.snackbar.error('Failed to create concept from import file.');
          }
        } else {
          await this.conceptService.createConcept(createdDesignOutput.file);
        }
      }
    }
  }

  private jobListeners(): void {
    this.actions.get(JobQueued).subscribe(async (message: JobMessageData) => {
      this.actions.execute(UpdateJobStatus, [
        message.jobId,
        message.jobStatus ?? '',
      ]);
    });
    this.actions.get(JobStarted).subscribe((message: JobMessageData) => {
      this.actions.execute(UpdateJobStatus, [
        message.jobId,
        (message?.['status'] as string) ?? '',
      ]);
    });
    this.actions.get(JobCreated).subscribe((message: any) => {
      this.actions.execute(AddJob, message?.jobId as string).then(() => {
        this.actions.execute(UpdateJobStatus, [
          message?.jobId as string,
          message?.jobStatus as string,
        ]);
      });
    });
    this.actions.get(JobCompleted).subscribe((message: JobMessageData) => {
      this.actions.execute(UpdateJobStatus, [
        message.jobId,
        message['status'] as string,
      ]);
    });

    this.actions.get(JobFinished).subscribe((message: JobMessageData) => {
      const job = message['job'];

      if (job) {
      // Force lastStatus to match the most recent entry in jobStatus array,
      // since it may still say 'RUNNING' even after a JobFinished event
        const latestStatus = job.jobStatus?.[job.jobStatus.length - 1]?.jobStatus;
        const jobToUpdate = latestStatus
          ? { ...job, lastStatus: latestStatus }
          : job;

        this.actions.execute(UpdateJob, jobToUpdate);
      }

      this.snackbar.info(
        `${job?.jobName ?? 'Job'} finished with status: ${
          job?.simulations?.[0].lastStatus
        }`
      );
    });

    this.actions.get(JobMessage).subscribe((message: JobMessageData) => {
      if (message['messagetype'] === 'progress') {
        this.actions.execute(UpdateJobProgress, message);
        this.actions.execute(UpdateJobTimes, message);
      }
    });
  }
}
